// src/utils/bluetooth-printer.js
// Direct Bluetooth ESC/POS printing for ZyWell ZM03 (57.5mm, 32 chars/line)
// Uses Web Bluetooth API — works on Chrome Android

const PRINTER_KEY = 'ps_bt_printer';
const CHAR_WIDTH = 32; // ZyWell ZM03 characters per line

// ESC/POS Commands
const ESC = 0x1B;
const GS = 0x1D;
const INIT = [ESC, 0x40];
const ALIGN_LEFT = [ESC, 0x61, 0x00];
const ALIGN_CENTER = [ESC, 0x61, 0x01];
const ALIGN_RIGHT = [ESC, 0x61, 0x02];
const BOLD_ON = [ESC, 0x45, 0x01];
const BOLD_OFF = [ESC, 0x45, 0x00];
const DOUBLE_SIZE = [ESC, 0x21, 0x11];
const NORMAL_SIZE = [ESC, 0x21, 0x00];
const FEED_LINE = [0x0A];
const FEED_LINES = (n) => [ESC, 0x64, n];
const CUT = [GS, 0x56, 0x42, 0x00];

// Save/load printer device info
export const savePrinterDevice = (name, id) => {
  localStorage.setItem(PRINTER_KEY, JSON.stringify({ name, id, savedAt: new Date().toISOString() }));
};
export const getSavedPrinter = () => {
  try { return JSON.parse(localStorage.getItem(PRINTER_KEY) || 'null'); } catch { return null; }
};
export const clearSavedPrinter = () => localStorage.removeItem(PRINTER_KEY);

// Connect to Bluetooth printer
export const connectPrinter = async () => {
  if (!navigator.bluetooth) {
    throw new Error('Web Bluetooth not supported. Use Chrome on Android.');
  }
  try {
    const device = await navigator.bluetooth.requestDevice({
      filters: [
        { name: 'Printer001' },
        { namePrefix: 'Printer' },
        { namePrefix: 'ZyWell' },
        { namePrefix: 'MP' },
        { namePrefix: 'RPP' },
      ],
      optionalServices: [
        '000018f0-0000-1000-8000-00805f9b34fb', // Common printer service
        '00001101-0000-1000-8000-00805f9b34fb', // Serial port
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Xprinter service
        '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip BM70
      ]
    });
    savePrinterDevice(device.name, device.id);
    return device;
  } catch (e) {
    if (e.name === 'NotFoundError') throw new Error('No printer selected. Please try again.');
    throw e;
  }
};

// Get GATT characteristic for writing
const getWriteCharacteristic = async (server) => {
  const serviceUUIDs = [
    '000018f0-0000-1000-8000-00805f9b34fb',
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
    '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  ];
  for (const uuid of serviceUUIDs) {
    try {
      const service = await server.getPrimaryService(uuid);
      const chars = await service.getCharacteristics();
      for (const char of chars) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          return char;
        }
      }
    } catch {}
  }
  // Try getting all services
  try {
    const services = await server.getPrimaryServices();
    for (const service of services) {
      const chars = await service.getCharacteristics();
      for (const char of chars) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          return char;
        }
      }
    }
  } catch {}
  throw new Error('Could not find printer write characteristic.');
};

// Text helpers for 32-char width
const pad = (str, width) => str.substring(0, width).padEnd(width, ' ');
const center = (str) => {
  const s = str.substring(0, CHAR_WIDTH);
  const pad = Math.floor((CHAR_WIDTH - s.length) / 2);
  return ' '.repeat(pad) + s;
};
const leftRight = (left, right) => {
  const space = CHAR_WIDTH - left.length - right.length;
  if (space <= 0) return (left + ' ' + right).substring(0, CHAR_WIDTH);
  return left + ' '.repeat(space) + right;
};
const divider = () => '='.repeat(CHAR_WIDTH);
const thinDivider = () => '-'.repeat(CHAR_WIDTH);

// Encode text to bytes
const encode = (text) => {
  const encoder = new TextEncoder();
  return Array.from(encoder.encode(text));
};

// Build bytes array
const bytes = (...args) => {
  const result = [];
  for (const arg of args) {
    if (Array.isArray(arg)) result.push(...arg);
    else if (typeof arg === 'string') result.push(...encode(arg));
    else result.push(arg);
  }
  return new Uint8Array(result);
};

// Send data in chunks (BLE max ~20 bytes per write)
const sendData = async (characteristic, data) => {
  const CHUNK = 100;
  for (let i = 0; i < data.length; i += CHUNK) {
    const chunk = data.slice(i, i + CHUNK);
    if (characteristic.properties.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
    } else {
      await characteristic.writeValue(chunk);
    }
    await new Promise(r => setTimeout(r, 20));
  }
};

// ── PRINT ENTRY RECEIPT ───────────────────────────────────
export const printEntryReceipt = async (device, record, settings) => {
  const server = await device.gatt.connect();
  const char = await getWriteCharacteristic(server);
  const hr = settings?.hourlyRate || 8;
  const buf = settings?.overtimeBufferMins || 10;
  const footer = settings?.receiptFooter || 'Thank you for parking!';

  const getLabel = pt => ({'monthly-24hr':'Monthly 24hr','monthly-night':'Monthly Night','monthly-day':'Monthly Day','weekly':'Weekly','biweekly':'Bi-Weekly','hourly':'Hourly','overnight':'Overnight','mou':'MOU'}[pt]||pt||'Hourly');

  const now = new Date(record.entryTime);
  const dateStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()}`;
  const timeStr = now.toLocaleTimeString('en-AE',{hour:'2-digit',minute:'2-digit',hour12:true});

  const data = bytes(
    INIT,
    ALIGN_CENTER, BOLD_ON, DOUBLE_SIZE,
    'AL BARAKAH\n',
    NORMAL_SIZE, BOLD_OFF,
    'Car Parking - Sharjah\n',
    divider() + '\n',
    BOLD_ON, '    ENTRY RECEIPT\n', BOLD_OFF,
    divider() + '\n',
    ALIGN_LEFT,
    leftRight('Date:', dateStr) + '\n',
    leftRight('Time:', timeStr) + '\n',
    BOLD_ON, leftRight('Plate:', record.plate) + '\n', BOLD_OFF,
    leftRight('Package:', getLabel(record.packageType)) + '\n',
    leftRight('Car Type:', record.carType==='open'?'Open':'Closed') + '\n',
    leftRight('Staff:', record.staffName||'—') + '\n',
    thinDivider() + '\n',
    ...(record.packageType==='hourly' ? [leftRight('Rate:', `AED ${hr}/hour`) + '\n', thinDivider() + '\n'] : []),
    ...(record.packageType==='overnight' ? [
      leftRight('Agreed Rate:', `AED ${record.overnightRate}`) + '\n',
      leftRight('Until:', record.overnightEndTime||'—') + '\n',
      ...(record.overnightAdvance>0 ? [leftRight('Advance Paid:', `AED ${record.overnightAdvance}`) + '\n'] : []),
      thinDivider() + '\n',
    ] : []),
    'TERMS & CONDITIONS\n',
    `1.AED ${hr}/hr.First hr full rate.\n`,
    `2.Grace ${buf}min=AED 2.After=${hr}AED.\n`,
    '3.No liability for loss/damage.\n',
    ...(record.packageType==='overnight' ? [`4.Extra time after ${record.overnightEndTime} = AED ${hr}/hr.\n`] : []),
    divider() + '\n',
    ALIGN_CENTER,
    footer + '\n',
    FEED_LINES(3),
    CUT
  );

  await sendData(char, data);
  server.disconnect();
};

// ── PRINT EXIT RECEIPT ────────────────────────────────────
export const printExitReceipt = async (device, record, settings) => {
  const server = await device.gatt.connect();
  const char = await getWriteCharacteristic(server);
  const footer = settings?.receiptFooter || 'Thank you for parking!';

  const getLabel = pt => ({'monthly-24hr':'Monthly 24hr','monthly-night':'Monthly Night','monthly-day':'Monthly Day','weekly':'Weekly','biweekly':'Bi-Weekly','hourly':'Hourly','overnight':'Overnight','mou':'MOU'}[pt]||pt||'Hourly');

  const exitDate = new Date(record.exitTime||new Date());
  const dateStr = `${String(exitDate.getDate()).padStart(2,'0')}/${String(exitDate.getMonth()+1).padStart(2,'0')}/${exitDate.getFullYear()}`;
  const exitTime = exitDate.toLocaleTimeString('en-AE',{hour:'2-digit',minute:'2-digit',hour12:true});
  const entryTime = record.entryTime ? new Date(record.entryTime).toLocaleTimeString('en-AE',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';

  const data = bytes(
    INIT,
    ALIGN_CENTER, BOLD_ON, DOUBLE_SIZE,
    'AL BARAKAH\n',
    NORMAL_SIZE, BOLD_OFF,
    'Car Parking - Sharjah\n',
    divider() + '\n',
    BOLD_ON, '     EXIT RECEIPT\n', BOLD_OFF,
    divider() + '\n',
    ALIGN_LEFT,
    leftRight('Date:', dateStr) + '\n',
    leftRight('Entry:', entryTime) + '\n',
    leftRight('Exit:', exitTime) + '\n',
    leftRight('Duration:', record.duration||'—') + '\n',
    BOLD_ON, leftRight('Plate:', record.plate||'—') + '\n', BOLD_OFF,
    leftRight('Package:', getLabel(record.packageType)) + '\n',
    thinDivider() + '\n',
    ...(record.overnightAdvance>0 ? [leftRight('Advance Paid:', `AED ${Number(record.overnightAdvance).toFixed(2)}`) + '\n'] : []),
    ...(record.discountAmount>0 ? [leftRight('Discount:', `-AED ${Number(record.discountAmount).toFixed(2)}`) + '\n'] : []),
    BOLD_ON, leftRight('TOTAL:', `AED ${Number(record.fee||0).toFixed(2)}`) + '\n', BOLD_OFF,
    ...(record.overnightFeeData?.extraFee>0 ? [BOLD_ON, leftRight('BALANCE DUE:', `AED ${Number(record.fee||0).toFixed(2)}`) + '\n', BOLD_OFF] : []),
    leftRight('Payment:', (record.paymentMethod||'—').toUpperCase()) + '\n',
    leftRight('Staff:', record.exitStaffName||record.staffName||'—') + '\n',
    divider() + '\n',
    ALIGN_CENTER,
    footer + '\n',
    FEED_LINES(3),
    CUT
  );

  await sendData(char, data);
  server.disconnect();
};

// ── PRINTER MANAGER (singleton device) ───────────────────
let _device = null;
let _disconnectListenerAttached = false;

export const getPrinterDevice = () => _device;
export const setPrinterDevice = (d) => {
  _device = d;
  attachDisconnectListener(d);
};

// Attach a listener so we KNOW when the printer drops mid-shift.
// This does not reconnect by itself (Chrome blocks auto-connect without
// a user gesture) but it clears stale state so the next print tap
// triggers a clean silent reconnect instead of failing on a dead handle.
const attachDisconnectListener = (device) => {
  if (!device || _disconnectListenerAttached) return;
  try {
    device.addEventListener('gattserverdisconnected', () => {
      // Connection dropped — leave _device in place so getDevices()/connect
      // can re-establish on next print without showing the picker.
      console.log('[printer] disconnected — will silently reconnect on next print');
    });
    _disconnectListenerAttached = true;
  } catch {}
};

// Silently resolve the saved printer device WITHOUT showing the picker.
// Uses navigator.bluetooth.getDevices() which returns already-permitted
// devices. Falls back to the in-memory handle if getDevices is unavailable.
const resolveSavedDevice = async () => {
  // 1. Already have a live handle in memory
  if (_device) return _device;

  const saved = getSavedPrinter();
  if (!saved) throw new Error('No printer set up yet. Open 🖨️ Printer in the menu and tap Connect once.');

  if (!navigator.bluetooth) {
    throw new Error('Bluetooth printing needs Chrome on Android.');
  }

  // 2. Try getDevices() — silent, no dialog
  if (navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      // Match by saved id first, then by name
      let match = devices.find(d => d.id === saved.id);
      if (!match) match = devices.find(d => d.name === saved.name);
      if (match) {
        _device = match;
        attachDisconnectListener(match);
        return match;
      }
    } catch (e) {
      // getDevices can throw if permission backend not enabled — fall through
      console.log('[printer] getDevices unavailable:', e.message);
    }
  }

  // 3. Could not silently resolve — printer permission needs to be granted
  //    once more via the Connect button (one tap, not every print).
  throw new Error('Tap 🖨️ Printer in the menu once to reconnect, then printing will be automatic again.');
};

// Ensure the GATT server is connected. Retries a couple of times because
// BLE first-connect after wake often fails on the first attempt.
const ensureConnected = async (device) => {
  if (device.gatt && device.gatt.connected) return;
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await device.gatt.connect();
      if (device.gatt.connected) return;
    } catch (e) {
      lastErr = e;
      await new Promise(r => setTimeout(r, 400)); // brief pause before retry
    }
  }
  throw new Error('Printer is off or out of range. Switch it on and tap print again.');
};

export const printWithSavedPrinter = async (type, record, settings) => {
  const device = await resolveSavedDevice();
  await ensureConnected(device);
  if (type === 'entry') await printEntryReceipt(device, record, settings);
  else await printExitReceipt(device, record, settings);
};

// Main entry point used by all print buttons.
// Silent reconnect via getDevices() + retrying connect. No picker dialog
// unless the printer permission genuinely needs re-granting.
export const autoReconnectAndPrint = async (type, record, settings) => {
  const device = await resolveSavedDevice();
  await ensureConnected(device);
  if (type === 'entry') await printEntryReceipt(device, record, settings);
  else await printExitReceipt(device, record, settings);
};
